import React, { useState, useEffect, useRef } from 'react'
import {ExternalLink, Sparkles} from 'lucide-react'

interface AdSquare {
  _id?: string
  squareId: number
  ownerAgentId?: string | null
  ownerAgentName?: string | null
  adImageUrl?: string | null
  websiteUrl?: string | null
  adContent?: string | null
}

export function XyberSpaceCarousel() {
  const [ads, setAds] = useState<AdSquare[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    loadAds()
  }, [])

  // Auto-scroll effect
  useEffect(() => {
    if (ads.length === 0 || isHovered) return

    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current
        const scrollAmount = container.offsetWidth / 3 // Scroll by one card width
        
        if (container.scrollLeft + container.offsetWidth >= container.scrollWidth - 10) {
          // Reset to start for infinite loop
          container.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        }
      }
    }, 3000) // Change ad every 3 seconds

    return () => clearInterval(interval)
  }, [ads.length, isHovered])

  const loadAds = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://api.lumi.new'}/v1/functions/${import.meta.env.VITE_PROJECT_ID || 'p411756259739451392'}/getSquares`
      )
      const data = await response.json()

      if (data.success) {
        // Filter only squares with images
        const adsWithImages = data.squares.filter(
          (sq: AdSquare) => sq.adImageUrl && sq.ownerAgentName
        )
        setAds(adsWithImages)
      }
    } catch (error) {
      console.error('Error loading ads:', error)
    }
  }

  const handleAdClick = (ad: AdSquare) => {
    if (ad.websiteUrl) {
      window.open(ad.websiteUrl, '_blank', 'noopener,noreferrer')
    } else if (ad.ownerAgentName) {
      const slug = ad.ownerAgentName.toLowerCase().replace(/\s+/g, '-')
      window.location.href = `/${slug}`
    }
  }

  const handlePlaceholderClick = () => {
    window.location.href = '/xyberspace'
  }

  // Always show carousel with placeholders if needed
  const displayItems = [...ads]
  const placeholdersNeeded = Math.max(0, 3 - ads.length)
  const placeholders = Array(placeholdersNeeded).fill(null).map((_, i) => ({ isPlaceholder: true, id: `placeholder-${i}` }))

  return (
    <div className="w-full bg-gradient-to-r from-deep-purple-950/40 via-deep-purple-900/40 to-deep-purple-950/40 border-b border-deep-purple-800 backdrop-blur-sm py-4">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-electric-purple-500 animate-pulse" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Xyber Space
          </span>
        </div>

        {/* Horizontal Scrolling Carousel */}
        <div
          ref={scrollContainerRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth justify-center"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {displayItems.map((ad) => (
            <div
              key={ad._id || ad.squareId}
              onClick={() => handleAdClick(ad)}
              className="flex-shrink-0 w-48 sm:w-52 md:w-56 group cursor-pointer"
            >
              <div className="relative bg-deep-purple-950/30 border border-deep-purple-800 rounded-lg overflow-hidden hover:border-electric-purple-500 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20 aspect-video">
                {/* Ad Image */}
                <img
                  src={ad.adImageUrl || ''}
                  alt={`Ad by ${ad.ownerAgentName}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-2 space-y-1">
                    <div className="text-xs text-white font-medium truncate">
                      {ad.ownerAgentName}
                    </div>
                    {ad.websiteUrl && (
                      <div className="flex items-center gap-1 text-electric-purple-400 text-xs">
                        <ExternalLink className="w-3 h-3" />
                        <span className="truncate">Visit Website</span>
                      </div>
                    )}
                  </div>
                </div>


              </div>
            </div>
          ))}
          
          {/* Placeholder Boxes */}
          {placeholders.map((placeholder: any) => (
            <div
              key={placeholder.id}
              onClick={handlePlaceholderClick}
              className="flex-shrink-0 w-48 sm:w-52 md:w-56 group cursor-pointer"
            >
              <div className="relative bg-deep-purple-950/50 border-2 border-dashed border-deep-purple-700 rounded-lg overflow-hidden hover:border-electric-purple-500 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20 aspect-video flex items-center justify-center">
                <div className="text-center p-4">
                  <Sparkles className="w-8 h-8 text-electric-purple-500 mx-auto mb-2 animate-pulse" />
                  <div className="text-sm font-bold text-white mb-1">Purchase Ad Space</div>
                  <div className="text-xs text-electric-purple-400 font-semibold">Click Here</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
